import React from 'react';
import { DEMO_BRANCHES } from './data/leads';
import storage from './services/storage';
import { applyHydrateRows } from './services/userDataSync';
import { generateId } from './utils/uuid';
import { supabase, isConfigured } from './services/supabase';
import { MittelBoostMark } from './components/BrandMark';

/* ── Konstanten ─────────────────────────────────────────────────────────── */
export const AUTH_USERS_KEY       = 'crm_auth_users';
export const AUTH_SESSION_KEY     = 'crm_auth_session';
export const DEFAULT_ADMIN_EMAIL  = 'robin@adstark.de';
export const DEFAULT_ADMIN_PASSWORD = 'Adstark';

/* ── Supabase-Profile → App-User Format ─────────────────────────────────── */
export function mapProfile(p) {
  return {
    id: p.id,
    name: p.name || '',
    email: p.email || '',
    role: p.role || 'viewer',
    salesRole: p.sales_role || p.salesRole || null,
    title: p.title || '',
    avatar: p.avatar || '',
    mustChangePassword: p.must_change_password || false,
    createdAt: p.created_at,
  };
}

/* ── Supabase Auth helpers ───────────────────────────────────────────────── */

export async function supabaseLogin(email, password) {
  const { data, error } = await supabase.auth.signInWithPassword({ email, password });
  if (error) throw error;
  return data;
}

export async function supabaseLogout() {
  await supabase.auth.signOut();
  storage.remove(AUTH_SESSION_KEY);
  storage.remove(AUTH_USERS_KEY);
}

export async function loadProfiles() {
  if (!isConfigured) return [];
  const { data, error } = await supabase.from('profiles').select('*');
  if (error) return [];
  return (data || []).map(mapProfile);
}

/**
 * Cloud → localStorage mit updated_at-Merge (neuere Seite gewinnt, Konflikt-Event bei lokalem Newer).
 * @returns {Promise<{ key: string, message: string }[]>} Konflikte für UI-Banner
 */
export async function hydrateUserData(userId) {
  if (!isConfigured) return [];
  const { data, error } = await supabase.from('user_data')
    .select('key, value, updated_at')
    .eq('user_id', userId);
  if (error) throw error;
  if (!data?.length) return [];
  return applyHydrateRows(data);
}

export async function updateUserRole(userId, role) {
  if (!isConfigured) return;
  await supabase.from('profiles').update({ role }).eq('id', userId);
}

export async function updateUserProfile(userId, changes) {
  if (!isConfigured) return;
  const allowed = {};
  if (changes.name  !== undefined) allowed.name  = changes.name;
  if (changes.title !== undefined) allowed.title = changes.title;
  if (changes.avatar !== undefined) allowed.avatar = changes.avatar;
  if (Object.keys(allowed).length === 0) return;
  await supabase.from('profiles').update(allowed).eq('id', userId);
}

export async function deleteSupabaseUser(userId) {
  if (!isConfigured) return;
  await supabase.from('profiles').delete().eq('id', userId);
  await supabase.from('user_data').delete().eq('user_id', userId);
}

export async function clearMustChangePassword(userId) {
  if (!isConfigured) return;
  await supabase.from('profiles').update({ must_change_password: false }).eq('id', userId);
}

export async function createSupabaseUser(email, password, name, role) {
  if (!isConfigured) throw new Error('Supabase nicht konfiguriert');

  const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL;
  const { data: { session: adminSession } } = await supabase.auth.getSession();

  // Try Edge Function first (creates a confirmed user, no email confirmation required)
  try {
    const res = await fetch(`${SUPABASE_URL}/functions/v1/create-user`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${adminSession?.access_token || ''}`,
      },
      body: JSON.stringify({ email, password, name, role }),
    });
    if (res.ok) {
      const user = await res.json();
      return user;
    }
    // 404 = function not yet deployed → fall through to signUp
    if (res.status !== 404) {
      const body = await res.json().catch(() => ({}));
      throw new Error(body.error || `HTTP ${res.status}`);
    }
  } catch (fetchErr) {
    // Network error or function not deployed — fall through
    if (fetchErr.message && !fetchErr.message.includes('fetch')) throw fetchErr;
  }

  // Fallback: signUp (works when email confirmation is disabled in Supabase)
  const { data, error } = await supabase.auth.signUp({
    email,
    password,
    options: { data: { name } },
  });
  if (error) throw error;

  if (data.user) {
    await supabase.from('profiles').upsert({
      id: data.user.id,
      email,
      name,
      role: role || 'viewer',
      must_change_password: true,
    });
  }

  // Restore admin session (signUp auto-logs-in the new user when confirmations are off)
  if (adminSession) {
    await supabase.auth.setSession({
      access_token: adminSession.access_token,
      refresh_token: adminSession.refresh_token,
    }).catch(() => {});
  }

  return data.user;
}

/* ── Helfer ─────────────────────────────────────────────────────────────── */
export function normalizeEmail(email = '') {
  return String(email).trim().toLowerCase();
}

export function loadAuthUsers() {
  const stored = storage.get(AUTH_USERS_KEY, []);
  return Array.isArray(stored) ? stored : [];
}

export function saveAuthUsers(users) {
  storage.set(AUTH_USERS_KEY, users);
}

export function ensureDefaultAdmin() {
  const users = loadAuthUsers();
  const adminEmail = normalizeEmail(DEFAULT_ADMIN_EMAIL);
  const adminExists = users.some(u => normalizeEmail(u.email) === adminEmail);
  if (adminExists) return users;
  const seeded = [
    ...users,
    {
      id: generateId(),
      name: 'Robin Admin',
      email: DEFAULT_ADMIN_EMAIL,
      password: DEFAULT_ADMIN_PASSWORD,
      title: 'Admin',
      avatar: '',
      role: 'admin',
      createdAt: new Date().toISOString(),
      generated: false,
    },
  ];
  saveAuthUsers(seeded);
  return seeded;
}

export function loadAuthSession() {
  const raw = storage.get(AUTH_SESSION_KEY, null);
  if (!(raw && raw.userId)) return null;
  const users = loadAuthUsers();
  const user = users.find(u => u.id === raw.userId);
  if (!user) {
    storage.remove(AUTH_SESSION_KEY);
    return null;
  }
  return { userId: user.id, email: user.email, role: user.role || 'viewer' };
}

export function saveAuthSession(session) {
  if (session) storage.set(AUTH_SESSION_KEY, session);
  else storage.remove(AUTH_SESSION_KEY);
}

const TEAM_WIDE_KEYS = new Set([
  'crm_leads',
  'crm_lists',
  'crm_tasks',
  'crm_meetings',
  'crm_pipeline_stages',
  'crm_team_views',
]);

export function getStorageKey(baseKey) {
  if (TEAM_WIDE_KEYS.has(baseKey)) return baseKey;
  const session = storage.get(AUTH_SESSION_KEY, null);
  if (!session?.userId) return baseKey;
  const users = storage.get(AUTH_USERS_KEY, []);
  const user = users.find(u => u.id === session.userId);
  if (!user || !user.role || user.role === 'admin') return baseKey;
  return `${baseKey}_u${session.userId}`;
}

export function getCurrentSessionRole() {
  const session = storage.get(AUTH_SESSION_KEY, null);
  if (!session?.userId) return null;
  const users = storage.get(AUTH_USERS_KEY, []);
  const user = users.find(u => u.id === session.userId);
  return user?.role || null;
}

/* ── Passwort-Änderung beim ersten Login ────────────────────────────────── */
export function ForcePasswordChangeScreen({ user, onComplete }) {
  const [pw, setPw]   = React.useState('');
  const [pw2, setPw2] = React.useState('');
  const [err, setErr] = React.useState('');

  function handleSubmit(e) {
    e.preventDefault();
    if (pw.length < 6) { setErr('Mindestens 6 Zeichen.'); return; }
    if (pw !== pw2)    { setErr('Passwörter stimmen nicht überein.'); return; }
    onComplete(pw);
  }

  return (
    <div style={{ minHeight:'100vh', display:'flex', alignItems:'center', justifyContent:'center', padding:24, background:'linear-gradient(145deg,var(--bg),var(--bg3))' }}>
      <div style={{ width:440, maxWidth:'96vw', borderRadius:18, background:'var(--bg2)', border:'1px solid var(--border)', boxShadow:'0 24px 56px rgba(15,23,42,0.18)', overflow:'hidden' }}>
        <div style={{ padding:'20px 22px 14px', borderBottom:'1px solid var(--border)', background:'linear-gradient(135deg,rgba(29,190,116,0.08),rgba(13,148,136,0.06))' }}>
          <div style={{ display:'flex', alignItems:'center', gap:10 }}>
            <div style={{ width:36, height:36, borderRadius:10, background:'rgba(29,190,116,0.12)', border:'1.5px solid rgba(29,190,116,0.2)', display:'flex', alignItems:'center', justifyContent:'center' }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#0f8a53" strokeWidth="2"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0110 0v4"/><circle cx="12" cy="16" r="1" fill="#0f8a53"/></svg>
            </div>
            <div>
              <div style={{ fontSize:17, fontWeight:700, color:'var(--text)' }}>Passwort festlegen</div>
              <div style={{ fontSize:12, color:'var(--text2)' }}>Bitte lege beim ersten Login ein eigenes Passwort fest</div>
            </div>
          </div>
        </div>
        <form onSubmit={handleSubmit} style={{ padding:22, display:'flex', flexDirection:'column', gap:12 }}>
          <div style={{ fontSize:13, color:'var(--text2)', lineHeight:1.5 }}>
            Hallo <b>{user.name || user.email}</b>, dein Zugang wurde neu angelegt.<br/>Bitte wähle jetzt ein eigenes Passwort.
          </div>
          <label style={{ display:'flex', flexDirection:'column', gap:6 }}>
            <span style={{ fontSize:12, fontWeight:600, color:'var(--text3)', textTransform:'uppercase', letterSpacing:'0.05em' }}>Neues Passwort</span>
            <input type="password" value={pw} onChange={e => { setPw(e.target.value); setErr(''); }} required
              style={{ height:40, borderRadius:10, border:'1.5px solid var(--border)', background:'var(--bg2)', padding:'0 12px', color:'var(--text)', fontSize:14, outline:'none' }} />
          </label>
          <label style={{ display:'flex', flexDirection:'column', gap:6 }}>
            <span style={{ fontSize:12, fontWeight:600, color:'var(--text3)', textTransform:'uppercase', letterSpacing:'0.05em' }}>Passwort bestätigen</span>
            <input type="password" value={pw2} onChange={e => { setPw2(e.target.value); setErr(''); }} required
              style={{ height:40, borderRadius:10, border:'1.5px solid var(--border)', background:'var(--bg2)', padding:'0 12px', color:'var(--text)', fontSize:14, outline:'none' }} />
          </label>
          {err && <div style={{ fontSize:12, color:'#dc2626', background:'rgba(220,38,38,0.08)', border:'1px solid rgba(220,38,38,0.2)', borderRadius:8, padding:'8px 10px' }}>{err}</div>}
          <button type="submit"
            style={{ height:42, marginTop:4, borderRadius:10, border:'none', background:'linear-gradient(135deg,#0f8a53,#159c5f)', color:'#fff', fontSize:14, fontWeight:700, cursor:'pointer', boxShadow:'0 6px 18px rgba(29,190,116,0.32)' }}>
            Passwort festlegen & anmelden
          </button>
        </form>
      </div>
    </div>
  );
}

/* ── Login-Screen ────────────────────────────────────────────────────────── */
export function LoginScreen({ error, onLogin }) {
  const [email,       setEmail]       = React.useState('');
  const [password,    setPassword]    = React.useState('');
  const [resetMode,   setResetMode]   = React.useState(false);
  const [resetEmail,  setResetEmail]  = React.useState('');
  const [resetStatus, setResetStatus] = React.useState('');
  const [resetMsg,    setResetMsg]    = React.useState('');
  const [focusedField, setFocusedField] = React.useState(null);

  // Load global branding (available to all users, including unauthenticated)
  const [loginBranding, setLoginBranding] = React.useState(() => {
    try { return JSON.parse(localStorage.getItem('crm_branding') || 'null') || null; } catch { return null; }
  });
  React.useEffect(() => {
    if (!isConfigured) return;
    supabase.from('branding').select('data').eq('id', 'global').single()
      .then(({ data }) => { if (data?.data && Object.keys(data.data).length > 0) setLoginBranding(data.data); })
      .catch(() => {});
  }, []);

  async function handleReset(e) {
    e.preventDefault();
    if (!resetEmail.trim()) return;
    try {
      const { error: err } = await supabase.auth.resetPasswordForEmail(resetEmail.trim(), {
        redirectTo: window.location.origin,
      });
      if (err) throw err;
      setResetStatus('sent');
      setResetMsg('E-Mail gesendet! Prüfe deinen Posteingang.');
    } catch (err) {
      setResetStatus('error');
      setResetMsg(err?.message || 'Fehler beim Senden.');
    }
  }

  function inputStyle(name) {
    const focused = focusedField === name;
    return {
      width: '100%', height: 50, borderRadius: 14,
      border: focused
        ? '1.5px solid rgba(130, 232, 185, 0.65)'
        : '1px solid rgba(100, 190, 150, 0.35)',
      background: focused
        ? 'linear-gradient(165deg, rgba(20, 62, 45, 0.72) 0%, rgba(13, 48, 35, 0.78) 100%)'
        : 'linear-gradient(165deg, rgba(16, 52, 38, 0.62) 0%, rgba(11, 40, 29, 0.68) 100%)',
      backdropFilter: 'blur(28px) saturate(200%) brightness(1.06)',
      WebkitBackdropFilter: 'blur(28px) saturate(200%) brightness(1.06)',
      padding: '0 16px',
      color: '#eaf2ff',
      WebkitTextFillColor: '#eaf2ff',
      caretColor: '#7fe3b4',
      fontSize: 15,
      outline: 'none',
      boxSizing: 'border-box',
      transition: 'border-color .2s, background .2s, box-shadow .2s',
      fontFamily: 'inherit',
      boxShadow: focused
        ? '0 0 0 3px rgba(29, 190, 116, 0.28), 0 8px 28px rgba(14, 96, 62, 0.45), inset 0 1px 0 rgba(255,255,255,0.22), inset 0 -1px 0 rgba(0,22,14,0.35)'
        : '0 4px 16px rgba(7, 28, 20, 0.35), inset 0 1px 0 rgba(255,255,255,0.14), inset 0 -1px 0 rgba(0,15,40,0.25)',
    };
  }

  return (
    <div style={{
      minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: '24px 16px',
      position: 'relative', overflow: 'hidden', background: '#060a12',
    }}>
      <style>{`
        @keyframes skyDay { 0%{opacity:0} 100%{opacity:1} }
        @keyframes sunOrb {
          0%   { transform:translateY(490px) scale(0.22); opacity:0; }
          5%   { opacity:0.65; }
          22%  { transform:translateY(280px) scale(0.52); opacity:1; }
          50%  { transform:translateY(120px) scale(0.80); opacity:1; }
          78%  { transform:translateY(-20px) scale(1.00); opacity:1; }
          100% { transform:translateY(-160px) scale(1.12); opacity:1; }
        }
        @keyframes horizonGlow {
          0%   { opacity:0; transform:scaleX(0.4) scaleY(0.3); }
          15%  { opacity:0.55; }
          50%  { opacity:0.85; transform:scaleX(0.90) scaleY(0.75); }
          100% { opacity:1;   transform:scaleX(1)    scaleY(1); }
        }
        @keyframes goldenWash {
          0%   { opacity:0; }
          18%  { opacity:0.10; }
          55%  { opacity:0.30; }
          100% { opacity:0.48; }
        }
        @keyframes veilLift {
          0%   { opacity:0.78; }
          30%  { opacity:0.50; }
          70%  { opacity:0.18; }
          100% { opacity:0.03; }
        }
        @keyframes cA { 0%,100%{transform:translateX(0px)}  45%{transform:translateX(28px)} }
        @keyframes cB { 0%,100%{transform:translateX(0px)}  55%{transform:translateX(-36px)} }
        @keyframes cC { 0%,100%{transform:translateX(0px)}  35%{transform:translateX(18px)}  70%{transform:translateX(-12px)} }
        @keyframes loginFadeIn { from{opacity:0;transform:translateY(22px) scale(0.97)} to{opacity:1;transform:none} }
        .login-field::placeholder { color: rgba(190, 235, 210, 0.58); }
        .login-field::selection { background: rgba(29, 190, 116, 0.45); color: #fff; }
        .login-btn:hover  { background:linear-gradient(135deg,rgba(29,190,116,0.92),rgba(90,205,150,0.85)) !important; box-shadow:0 14px 34px rgba(15,138,83,0.55),inset 0 1px 0 rgba(255,255,255,0.3) !important; transform:translateY(-1px); }
        .login-btn:active { transform:translateY(0) !important; }
        @media(max-width:480px){
          .login-card { border-radius:22px !important; }
          .login-card-header { padding:22px 18px 12px !important; }
          .login-card-body   { padding:0 18px 22px !important; }
        }
      `}</style>

      {/* ── SCENE: full-viewport SVG mountain sunrise ── */}
      <svg style={{ position:'fixed', inset:0, width:'100%', height:'100%', pointerEvents:'none', zIndex:0 }}
        viewBox="0 0 1920 1080" preserveAspectRatio="xMidYMid slice">
        <defs>
          <linearGradient id="skyNight" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%"   stopColor="#04100a"/>
            <stop offset="55%"  stopColor="#0a1c14"/>
            <stop offset="80%"  stopColor="#1a1008"/>
            <stop offset="100%" stopColor="#200a00"/>
          </linearGradient>
          <linearGradient id="skyDay" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%"   stopColor="#12362a"/>
            <stop offset="28%"  stopColor="#1f6d52"/>
            <stop offset="52%"  stopColor="#c87028"/>
            <stop offset="70%"  stopColor="#e89030"/>
            <stop offset="86%"  stopColor="#f8b840"/>
            <stop offset="100%" stopColor="#ffd060"/>
          </linearGradient>
          {/* Bright sun core */}
          <radialGradient id="sunCore" cx="50%" cy="50%" r="50%">
            <stop offset="0%"   stopColor="#ffffff"  stopOpacity="1"/>
            <stop offset="10%"  stopColor="#fffde0"  stopOpacity="1"/>
            <stop offset="28%"  stopColor="#ffe84a"  stopOpacity="1"/>
            <stop offset="52%"  stopColor="#ffb020"  stopOpacity="0.95"/>
            <stop offset="74%"  stopColor="#ff6e00"  stopOpacity="0.55"/>
            <stop offset="90%"  stopColor="#ff2800"  stopOpacity="0.15"/>
            <stop offset="100%" stopColor="#ff0000"  stopOpacity="0"/>
          </radialGradient>
          {/* Mid glow layer */}
          <radialGradient id="sunGlow" cx="50%" cy="50%" r="50%">
            <stop offset="0%"   stopColor="#ffe880"  stopOpacity="0.75"/>
            <stop offset="38%"  stopColor="#ffaa30"  stopOpacity="0.42"/>
            <stop offset="70%"  stopColor="#ff5500"  stopOpacity="0.14"/>
            <stop offset="100%" stopColor="#ff1000"  stopOpacity="0"/>
          </radialGradient>
          {/* Outer solar flare */}
          <radialGradient id="sunFlare" cx="50%" cy="50%" r="50%">
            <stop offset="0%"   stopColor="#ffd040"  stopOpacity="0.38"/>
            <stop offset="40%"  stopColor="#ff8020"  stopOpacity="0.14"/>
            <stop offset="78%"  stopColor="#ff3000"  stopOpacity="0.04"/>
            <stop offset="100%" stopColor="#ff1000"  stopOpacity="0"/>
          </radialGradient>
          {/* Horizon corona */}
          <radialGradient id="corona" cx="50%" cy="100%" r="60%">
            <stop offset="0%"   stopColor="#ffd040" stopOpacity="0.98"/>
            <stop offset="28%"  stopColor="#ff9020" stopOpacity="0.72"/>
            <stop offset="62%"  stopColor="#ff4800" stopOpacity="0.32"/>
            <stop offset="100%" stopColor="#600010" stopOpacity="0"/>
          </radialGradient>
          <radialGradient id="cloudA" cx="50%" cy="50%" r="50%">
            <stop offset="0%"  stopColor="#fff5e0" stopOpacity="0.55"/>
            <stop offset="60%" stopColor="#ffe8c0" stopOpacity="0.18"/>
            <stop offset="100%" stopColor="white"  stopOpacity="0"/>
          </radialGradient>
          <radialGradient id="cloudB" cx="50%" cy="50%" r="50%">
            <stop offset="0%"  stopColor="#ffe0c0" stopOpacity="0.40"/>
            <stop offset="55%" stopColor="#ffc080" stopOpacity="0.12"/>
            <stop offset="100%" stopColor="white"  stopOpacity="0"/>
          </radialGradient>
        </defs>

        {/* Base night sky */}
        <rect width="1920" height="1080" fill="url(#skyNight)"/>
        {/* Day sky fades in */}
        <rect width="1920" height="1080" fill="url(#skyDay)"
          style={{ animation:'skyDay 22s ease-in forwards', opacity:0 }}/>

        {/* Stars */}
        {[[120,80],[340,55],[580,95],[820,40],[1100,65],[1340,45],[1600,80],[1780,55],[200,130],[960,30],[1450,110]].map(([x,y],i) => (
          <circle key={i} cx={x} cy={y} r={i%3===0?1.8:1.2} fill="#e8f0ff" opacity="0.6"/>
        ))}

        {/* Horizon corona */}
        <ellipse cx="960" cy="1080" rx="820" ry="650" fill="url(#corona)"
          style={{ animation:'horizonGlow 22s cubic-bezier(0.15,0,0.4,1) forwards', opacity:0 }}/>

        {/* ── SUN — drawn BEFORE mountains so it rises from behind them ── */}
        {/* Outer flare (huge, soft) */}
        <circle cx="960" cy="390" r="520" fill="url(#sunFlare)"
          style={{ animation:'sunOrb 22s cubic-bezier(0.20,0,0.42,1) forwards', opacity:0 }}/>
        {/* Mid glow */}
        <circle cx="960" cy="390" r="270" fill="url(#sunGlow)"
          style={{ animation:'sunOrb 22s cubic-bezier(0.20,0,0.42,1) forwards', opacity:0 }}/>
        {/* Bright core */}
        <circle cx="960" cy="390" r="100" fill="url(#sunCore)"
          style={{ animation:'sunOrb 22s cubic-bezier(0.20,0,0.42,1) forwards', opacity:0 }}/>

        {/* ── Far background mountains (bluish, hazy) ── */}
        <path d="M0 1080 L0 560 L80 510 L180 460 L280 490 L380 420 L490 380 L580 410 L680 350 L760 380 L850 310 L920 340 L960 295 L1000 340 L1070 310 L1160 380 L1240 350 L1340 410 L1430 380 L1530 420 L1640 460 L1740 490 L1840 510 L1920 560 L1920 1080 Z"
          fill="#16362a" opacity="0.7"/>

        {/* ── Mid-range mountains (main dramatic peaks) ── */}
        <path d="M0 1080 L0 660 L100 620 L220 640 L330 580 L430 600 L540 520 L640 555 L730 470 L810 500 L880 420 L940 450 L960 390 L980 450 L1040 420 L1110 500 L1190 470 L1280 555 L1380 520 L1480 600 L1590 580 L1700 640 L1820 620 L1920 660 L1920 1080 Z"
          fill="#0c1a14" opacity="0.92"/>

        {/* ── Clouds — layer A (left of peak) ── */}
        <g style={{ animation:'cA 45s ease-in-out infinite' }}>
          <ellipse cx="650" cy="410" rx="210" ry="65" fill="url(#cloudA)"/>
          <ellipse cx="720" cy="385" rx="140" ry="48" fill="url(#cloudA)" opacity="0.7"/>
          <ellipse cx="590" cy="425" rx="120" ry="40" fill="url(#cloudA)" opacity="0.5"/>
        </g>
        {/* ── Clouds — layer B (right of peak) ── */}
        <g style={{ animation:'cB 58s ease-in-out infinite' }}>
          <ellipse cx="1280" cy="390" rx="230" ry="70" fill="url(#cloudB)"/>
          <ellipse cx="1210" cy="368" rx="155" ry="50" fill="url(#cloudB)" opacity="0.65"/>
          <ellipse cx="1350" cy="408" rx="110" ry="38" fill="url(#cloudB)" opacity="0.50"/>
        </g>
        {/* ── Clouds — layer C (wisps around peak) ── */}
        <g style={{ animation:'cC 32s ease-in-out infinite' }}>
          <ellipse cx="960" cy="370" rx="160" ry="42" fill="url(#cloudA)" opacity="0.45"/>
          <ellipse cx="900" cy="355" rx="90"  ry="30" fill="url(#cloudA)" opacity="0.30"/>
          <ellipse cx="1030" cy="358" rx="100" ry="28" fill="url(#cloudA)" opacity="0.28"/>
        </g>

        {/* ── Foreground silhouette ── */}
        <path d="M0 1080 L0 800 L60 790 L120 780 L180 770 L250 785 L320 762 L400 778 L480 750 L560 770 L640 745 L720 760 L800 740 L870 755 L920 748 L960 742 L1000 748 L1050 755 L1120 740 L1200 760 L1280 745 L1360 770 L1440 750 L1520 778 L1600 762 L1680 785 L1740 770 L1800 780 L1860 790 L1920 800 L1920 1080 Z"
          fill="#060d09"/>

        {/* Golden wash */}
        <rect width="1920" height="1080" fill="rgba(255,160,30,1)"
          style={{ animation:'goldenWash 22s ease-in forwards', opacity:0 }}/>
      </svg>

      {/* Dark veil that lifts as the sun rises */}
      <div style={{
        position:'fixed', inset:0, background:'rgba(4,6,10,0.78)',
        pointerEvents:'none', zIndex:1,
        animation:'veilLift 22s ease-in forwards',
      }} />

      {/* Glass card */}
      <div className="login-card" style={{
        width: 420, maxWidth: '100%',
        background: 'linear-gradient(155deg, rgba(13, 48, 35, 0.52) 0%, rgba(8, 32, 23, 0.58) 45%, rgba(11, 40, 29, 0.5) 100%)',
        backdropFilter: 'blur(56px) saturate(210%) brightness(1.05)',
        WebkitBackdropFilter: 'blur(56px) saturate(210%) brightness(1.05)',
        borderRadius: 32,
        border: '1px solid rgba(140, 235, 190, 0.28)',
        boxShadow: [
          '0 32px 80px rgba(0, 14, 9, 0.72)',
          '0 0 0 0.5px rgba(120, 230, 180, 0.12)',
          'inset 0 1.5px 0 rgba(255,255,255,0.32)',
          'inset 0 -1px 0 rgba(0, 22, 14, 0.28)',
          'inset 1px 0 0 rgba(255,255,255,0.08)',
          'inset -1px 0 0 rgba(255,255,255,0.05)',
        ].join(', '),
        overflow: 'hidden',
        animation: 'loginFadeIn .5s cubic-bezier(0.22,1,0.36,1)',
        position: 'relative', zIndex: 2,
      }}>
        {/* Top sheen highlight */}
        <div style={{ position:'absolute', top:0, left:'10%', right:'10%', height:1.5, background:'linear-gradient(90deg, transparent, rgba(255,255,255,0.55), transparent)', pointerEvents:'none', borderRadius:999 }} />

        {/* Inner top-left corner light catch */}
        <div style={{ position:'absolute', top:0, left:0, width:160, height:160, background:'radial-gradient(circle at top left, rgba(255,255,255,0.07) 0%, transparent 65%)', pointerEvents:'none' }} />

        {/* Header */}
        <div className="login-card-header" style={{ padding: '26px 30px 16px', textAlign: 'center' }}>
          {/* Icon or custom logo */}
          {loginBranding?.loginMode === 'image' && loginBranding?.loginLogo ? (
            <img src={loginBranding.loginLogo} alt="Logo"
              style={{ maxHeight: 56, maxWidth: '80%', objectFit: 'contain', display: 'block', margin: '0 auto 14px' }} />
          ) : (
            <div style={{ display:'inline-flex', alignItems:'center', justifyContent:'center', width:54, height:54, borderRadius:17, background:'rgba(9,30,22,0.55)', border:'1px solid rgba(140,235,190,0.35)', backdropFilter:'blur(16px)', WebkitBackdropFilter:'blur(16px)', marginBottom:14, boxShadow:'0 6px 24px rgba(15,138,83,0.28), inset 0 1.5px 0 rgba(255,255,255,0.28)' }}>
              <MittelBoostMark size={30} variant="dark" withLines={false} />
            </div>
          )}
          {/* Title */}
          {loginBranding?.loginMode === 'image' && loginBranding?.loginLogo ? null : (
            <div style={{
              fontSize: loginBranding?.loginMode === 'text' && loginBranding?.loginTitle ? (loginBranding.loginTitleSize || 26) : 26,
              fontWeight: 600,
              color: loginBranding?.loginMode === 'text' && loginBranding?.loginTitle ? (loginBranding.loginTitleColor || '#fff') : '#fff',
              letterSpacing: '-0.03em', marginBottom: 6, textShadow:'0 2px 12px rgba(0,0,0,0.4)',
            }}>
              {(loginBranding?.loginMode === 'text' && loginBranding?.loginTitle)
                ? loginBranding.loginTitle
                : <>Mittel<span style={{ fontWeight: 800, color: '#1DBE74' }}>Boost</span></>}
            </div>
          )}
          <div style={{ fontSize: 14, color: 'rgba(205,235,220,0.6)', fontWeight: 400 }}>
            {resetMode ? 'Passwort zurücksetzen' : 'Willkommen zurück'}
          </div>
        </div>

        {/* Form */}
        <div className="login-card-body" style={{ padding: '0 28px 26px' }}>
          {!resetMode ? (
            <form onSubmit={e => { e.preventDefault(); onLogin(email, password); }} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
                <span style={{ fontSize: 11.5, fontWeight: 600, color: 'rgba(205,235,220,0.55)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>E-Mail</span>
                <input className="login-field" type="email" value={email} onChange={e => setEmail(e.target.value)} required
                  placeholder="deine@email.de"
                  onFocus={() => setFocusedField('email')}
                  onBlur={() => setFocusedField(null)}
                  style={inputStyle('email')} />
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontSize: 11.5, fontWeight: 600, color: 'rgba(205,235,220,0.55)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Passwort</span>
                  <button type="button" onClick={() => { setResetMode(true); setResetEmail(email); setResetStatus(''); setResetMsg(''); }}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 12, color: 'rgba(150,225,185,0.85)', fontWeight: 500, textDecoration: 'underline', textDecorationColor: 'rgba(150,225,185,0.3)', textUnderlineOffset: 3 }}>
                    Passwort vergessen?
                  </button>
                </div>
                <input className="login-field" type="password" value={password} onChange={e => setPassword(e.target.value)} required
                  placeholder="••••••••"
                  onFocus={() => setFocusedField('password')}
                  onBlur={() => setFocusedField(null)}
                  style={inputStyle('password')} />
              </div>

              {error && (
                <div style={{ fontSize: 13, color: '#fca5a5', background: 'rgba(220,38,38,0.18)', border: '1px solid rgba(220,38,38,0.35)', borderRadius: 12, padding: '10px 14px', backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)' }}>
                  {error}
                </div>
              )}

              <button type="submit" className="login-btn" style={{
                height: 52, marginTop: 2, borderRadius: 16,
                border: '1px solid rgba(120,230,180,0.45)',
                background: 'linear-gradient(135deg, rgba(15,138,83,0.82), rgba(29,190,116,0.72))',
                backdropFilter: 'blur(16px)', WebkitBackdropFilter: 'blur(16px)',
                color: '#fff', fontSize: 15, fontWeight: 700, cursor: 'pointer',
                boxShadow: '0 8px 28px rgba(15,138,83,0.45), inset 0 1.5px 0 rgba(255,255,255,0.28)',
                letterSpacing: '0.02em', transition: 'all .18s ease',
              }}>
                Anmelden
              </button>
            </form>
          ) : (
            <form onSubmit={handleReset} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div style={{ fontSize: 13, color: 'rgba(205,235,220,0.65)', lineHeight: 1.65, marginBottom: 2 }}>
                Gib deine E-Mail-Adresse ein. Wir senden dir einen Link zum Zurücksetzen.
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
                <span style={{ fontSize: 11.5, fontWeight: 600, color: 'rgba(205,235,220,0.55)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>E-Mail</span>
                <input className="login-field" type="email" value={resetEmail} onChange={e => setResetEmail(e.target.value)} required
                  placeholder="deine@email.de"
                  onFocus={() => setFocusedField('resetEmail')}
                  onBlur={() => setFocusedField(null)}
                  style={inputStyle('resetEmail')} />
              </div>

              {resetMsg && (
                <div style={{ fontSize: 13, color: resetStatus === 'sent' ? '#86efac' : '#fca5a5', background: resetStatus === 'sent' ? 'rgba(22,163,74,0.18)' : 'rgba(220,38,38,0.18)', border: `1px solid ${resetStatus === 'sent' ? 'rgba(22,163,74,0.35)' : 'rgba(220,38,38,0.35)'}`, borderRadius: 12, padding: '10px 14px', backdropFilter: 'blur(12px)' }}>
                  {resetMsg}
                </div>
              )}

              <button type="submit" disabled={resetStatus === 'sent'} className="login-btn" style={{
                height: 52, borderRadius: 16,
                border: '1px solid rgba(120,230,180,0.45)',
                background: resetStatus === 'sent' ? 'rgba(22,163,74,0.35)' : 'linear-gradient(135deg, rgba(15,138,83,0.82), rgba(29,190,116,0.72))',
                backdropFilter: 'blur(16px)', WebkitBackdropFilter: 'blur(16px)',
                color: '#fff', fontSize: 15, fontWeight: 700,
                cursor: resetStatus === 'sent' ? 'default' : 'pointer',
                boxShadow: '0 8px 28px rgba(15,138,83,0.35), inset 0 1.5px 0 rgba(255,255,255,0.25)',
                transition: 'all .18s ease',
              }}>
                {resetStatus === 'sent' ? '✓ E-Mail gesendet' : 'Link senden'}
              </button>

              <button type="button" onClick={() => setResetMode(false)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, color: 'rgba(205,235,220,0.55)', textAlign: 'center', marginTop: -4 }}>
                ← Zurück zum Login
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

/* ── Demo-Branche wählen ────────────────────────────────────────────────── */
export function DemoBranchScreen({ onComplete }) {
  const [selected, setSelected] = React.useState(null);

  return (
    <div style={{ minHeight:'100vh', display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', padding:24, background:'linear-gradient(145deg,var(--bg),var(--bg3))' }}>
      <div style={{ width:'100%', maxWidth:680 }}>

        {/* Header */}
        <div style={{ textAlign:'center', marginBottom:28 }}>
          <div style={{ display:'inline-flex', alignItems:'center', justifyContent:'center', width:56, height:56, borderRadius:16, background:'rgba(29,190,116,0.12)', border:'1.5px solid rgba(29,190,116,0.22)', marginBottom:14 }}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#0f8a53" strokeWidth="2"><path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>
          </div>
          <div style={{ fontSize:22, fontWeight:800, color:'var(--text)', marginBottom:6 }}>In welcher Branche bist du tätig?</div>
          <div style={{ fontSize:14, color:'var(--text2)', lineHeight:1.6 }}>
            Wir füllen deinen Demo-Account mit passenden Musterfirmen,<br/>
            realitätsnahen Leads und Terminen aus deiner Branche.
          </div>
        </div>

        {/* Branch grid */}
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(190px,1fr))', gap:10, marginBottom:22 }}>
          {DEMO_BRANCHES.map(b => {
            const active = selected === b.key;
            return (
              <button key={b.key} onClick={() => setSelected(b.key)}
                style={{
                  padding:'14px 12px', borderRadius:12, cursor:'pointer', textAlign:'left',
                  border: `2px solid ${active ? '#159c5f' : 'var(--border)'}`,
                  background: active ? 'rgba(29,190,116,0.08)' : 'var(--bg2)',
                  boxShadow: active ? '0 0 0 3px rgba(29,190,116,0.12)' : 'none',
                  transition:'all .14s',
                }}
                onMouseEnter={e => { if (!active) { e.currentTarget.style.borderColor = 'rgba(29,190,116,0.4)'; e.currentTarget.style.background = 'var(--bg3)'; } }}
                onMouseLeave={e => { if (!active) { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.background = 'var(--bg2)'; } }}>
                <div style={{ fontSize:24, marginBottom:6 }}>{b.icon}</div>
                <div style={{ fontSize:13, fontWeight:700, color: active ? '#0f8a53' : 'var(--text)', marginBottom:2 }}>{b.label}</div>
                <div style={{ fontSize:11, color:'var(--text3)', lineHeight:1.4 }}>{b.sub}</div>
              </button>
            );
          })}
        </div>

        {/* Confirm */}
        <div style={{ display:'flex', justifyContent:'center' }}>
          <button
            onClick={() => { if (selected) onComplete(selected); }}
            disabled={!selected}
            style={{
              height:46, padding:'0 36px', borderRadius:12, border:'none', fontSize:15, fontWeight:700, cursor: selected ? 'pointer' : 'default',
              background: selected ? 'linear-gradient(135deg,#0f8a53,#159c5f)' : 'var(--bg4)',
              color: selected ? '#fff' : 'var(--text3)',
              boxShadow: selected ? '0 6px 20px rgba(29,190,116,0.35)' : 'none',
              transition:'all .15s',
            }}>
            {selected ? `${DEMO_BRANCHES.find(b=>b.key===selected)?.icon} Demo starten` : 'Branche auswählen'}
          </button>
        </div>
      </div>
    </div>
  );
}
